import React from 'react';
import { Alert, Button, Checkbox, DatePicker, Popconfirm, Select, Space, Table, Tag, message } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { useQuery } from 'react-query';
import moment from 'moment';
import MainContent from '@/layouts/MainContent';
import useHasPermissions from '@/hooks/useHasPermissions';
import { APP_PERMISSIONS } from '@/services/roleService';
import { DEFAULT_DISPLAY_DATE_TIME_FORMAT } from '@/config';
import { formatCurrency } from '@/helpers/format';
import { defaultApiErrorHandle } from '@/helpers/errorHandler';
import { ControlPriceGapByAgentModel, ControlReviewCategory, ControlReviewItemModel } from '@/__generated';
import { apiSdk } from '@/httpClient';

const PAGE_TITLE = 'controlReviews_Title';

const CATEGORIES = [
  { value: 0, label: 'controlReviews_CategoryAll' },
  { value: 1, label: 'controlReviews_CategoryPriceGap' },
  { value: 2, label: 'controlReviews_CategoryBoarding' },
  { value: 3, label: 'controlReviews_CategoryTime' },
  { value: 4, label: 'controlReviews_CategoryManual' },
  { value: 6, label: 'controlReviews_CategoryOnlineManual' },
  { value: 7, label: 'controlReviews_CategoryTerminal' },
  { value: 5, label: 'controlReviews_CategoryOther' },
];

const formatLocal = (v?: string | Date) => (v ? moment(v).format(DEFAULT_DISPLAY_DATE_TIME_FORMAT) : '');

/**
 * Actions hors ligne gardées mais signalées : écarts de prix par chauffeur, montées sans contrôle, heures incohérentes,
 * saisies du numéro. La revue garde la date et l'auteur, le signalement reste pour l'historique.
 */
export default function ControlReviewsPage() {
  const { t } = useTranslation();
  const [category, setCategory] = React.useState(0);
  const [includeReviewed, setIncludeReviewed] = React.useState(false);
  const [agentUserId, setAgentUserId] = React.useState<string>();
  const [period, setPeriod] = React.useState<any>();
  const [reviewing, setReviewing] = React.useState<string>();
  const canReview = useHasPermissions([APP_PERMISSIONS.ManageTickets]);
  const from = period?.[0]?.startOf('day').toISOString();
  const to = period?.[1]?.endOf('day').toISOString();

  const { data, isLoading, refetch } = useQuery(['company_control_reviews', category, includeReviewed, agentUserId, from, to], () =>
    apiSdk.ControlReviewService.controlReviews({ category: category as ControlReviewCategory, includeReviewed, agentUserId, from, to }),
  );
  const { data: gaps, refetch: refetchGaps } = useQuery(['company_control_price_gaps', includeReviewed], () =>
    apiSdk.ControlReviewService.priceGaps({ includeReviewed }),
  );

  const review = (item: ControlReviewItemModel) => {
    setReviewing(item.id);
    apiSdk.ControlReviewService.reviewed({ source: item.source!, id: item.id! })
      .then(response => {
        if (response.success) {
          message.success(t('controlReviews_Reviewed'));
          refetch();
          refetchGaps();
        } else {
          defaultApiErrorHandle(response);
        }
      })
      .catch(defaultApiErrorHandle)
      .finally(() => setReviewing(undefined));
  };

  const gapColumns = [
    { title: t('controlReviews_Agent'), dataIndex: 'agentName' },
    { title: t('controlReviews_Count'), dataIndex: 'count' },
    { title: t('controlReviews_Declared'), dataIndex: 'declaredAmount', render: (v: number) => formatCurrency(v) },
    { title: t('controlReviews_Expected'), dataIndex: 'expectedAmount', render: (v: number) => formatCurrency(v) },
    { title: t('controlReviews_Gap'), dataIndex: 'amountGap', render: (v: number) => <b>{formatCurrency(v)}</b> },
    {
      title: '',
      key: 'select',
      render: (_: any, item: ControlPriceGapByAgentModel) => (
        <Button
          size="small"
          data-testid={`agent-gaps-${item.agentUserId}`}
          onClick={() => {
            setAgentUserId(item.agentUserId);
            setCategory(1);
          }}
        >
          {t('controlReviews_SeeDetails')}
        </Button>
      ),
    },
  ];

  const columns = [
    { title: t('controlReviews_RecordedAt'), dataIndex: 'recordedAt', render: formatLocal },
    { title: t('controlReviews_Agent'), dataIndex: 'agentName' },
    { title: t('controlReviews_Terminal'), dataIndex: 'terminalName', render: (v?: string) => v || '—' },
    { title: t('controlReviews_Ticket'), dataIndex: 'ticketRefNo', render: (v?: string) => v || '—' },
    {
      title: t('controlReviews_Kind'),
      dataIndex: 'kind',
      render: (v?: number) => t(v === 1 ? 'controlReviews_KindCheckIn' : v === 2 ? 'controlReviews_KindBoarding' : 'controlReviews_KindSale'),
    },
    {
      title: t('controlReviews_Flags'),
      dataIndex: 'flags',
      render: (flags: string[], item: ControlReviewItemModel) => (
        <>
          {(flags || []).map(f => (
            <Tag key={f}>{t(`controlReviews_flag_${f}`)}</Tag>
          ))}
          {item.manualReason ? <Tag>{item.manualReason}</Tag> : null}
        </>
      ),
    },
    {
      title: t('controlReviews_Result'),
      key: 'result',
      render: (_: any, item: ControlReviewItemModel) =>
        item.accepted ? <Tag color="green">{t('controlReviews_Kept')}</Tag> : <Tag color="red">{t(item.refusalCode || 'controlReviews_Refused')}</Tag>,
    },
    { title: t('controlReviews_Declared'), dataIndex: 'declaredAmount', render: (v?: number) => (v == null ? '—' : formatCurrency(v)) },
    { title: t('controlReviews_Expected'), dataIndex: 'expectedAmount', render: (v?: number) => (v == null ? '—' : formatCurrency(v)) },
    { title: t('controlReviews_Gap'), dataIndex: 'amountGap', render: (v?: number) => (v == null ? '—' : formatCurrency(v)) },
    {
      title: t('controlReviews_Review'),
      key: 'review',
      render: (_: any, item: ControlReviewItemModel) =>
        item.reviewedOn ? (
          <span>{t('controlReviews_ReviewedBy', { name: item.reviewedBy || '—', date: formatLocal(item.reviewedOn) })}</span>
        ) : canReview ? (
          <Popconfirm
            title={t('controlReviews_ConfirmReview')}
            okText={t('controlReviews_MarkReviewed')}
            cancelText={t('cancel')}
            onConfirm={() => review(item)}
          >
            <Button size="small" icon={<CheckOutlined />} loading={reviewing === item.id} data-testid={`review-${item.id}`}>
              {t('controlReviews_MarkReviewed')}
            </Button>
          </Popconfirm>
        ) : (
          '—'
        ),
    },
  ];

  return (
    <MainContent title={t(PAGE_TITLE)}>
      <Helmet title={t(PAGE_TITLE)} />
      <Alert type="info" showIcon style={{ marginBottom: 12 }} message={t('controlReviews_Explanation')} />
      <h3>{t('controlReviews_GapsByAgent')}</h3>
      <Table<ControlPriceGapByAgentModel>
        rowKey="agentUserId"
        dataSource={gaps || []}
        columns={gapColumns as any}
        pagination={false}
        locale={{ emptyText: t('controlReviews_NoGaps') }}
        bordered
        size="small"
        style={{ marginBottom: 24 }}
      />
      <Space wrap style={{ marginBottom: 12 }}>
        <Select
          style={{ width: 260 }}
          value={category}
          onChange={setCategory}
          options={CATEGORIES.map(c => ({ value: c.value, label: t(c.label) }))}
          data-testid="category"
        />
        <DatePicker.RangePicker value={period} onChange={setPeriod} />
        {agentUserId ? (
          <Tag closable onClose={() => setAgentUserId(undefined)}>
            {gaps?.find(x => x.agentUserId === agentUserId)?.agentName || t('controlReviews_Agent')}
          </Tag>
        ) : null}
        <Checkbox checked={includeReviewed} onChange={e => setIncludeReviewed(e.target.checked)}>
          {t('controlReviews_IncludeReviewed')}
        </Checkbox>
      </Space>
      <Table<ControlReviewItemModel>
        rowKey="id"
        loading={isLoading}
        dataSource={data || []}
        columns={columns as any}
        pagination={{ pageSize: 50 }}
        locale={{ emptyText: t('controlReviews_None') }}
        bordered
        size="middle"
      />
    </MainContent>
  );
}
